

import { Vue, Component, Prop } from "vue-property-decorator";
import timeRangeForNew from "@/helpers/cardInfoHelperFunctions/timeRangeForNew";

import GET_DASHBOARD from "@/apollo/dashboard/GetDashboardUpdate.gql";

import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import DashboardContractors from "@/components/dashboard/cards/DashboardContractors.vue";
import DashboardContractorsManagerView from "@/components/dashboard/cards/DashboardContractorsManagerView.vue";
import DashboardManagers from "@/components/dashboard/cards/DashboardManagers.vue";
import DashboardFavorites from "@/components/dashboard/cards/DashboardFavorites.vue";
import DashboardProjects from "@/components/dashboard/cards/DashboardProjects.vue";
import DashboardGroups from "@/components/dashboard/cards/DashboardGroups.vue";
import DashboardPostings from "@/components/dashboard/cards/DashboardPostings.vue";
import DashboardMarketplace from "@/components/dashboard/cards/DashboardMarketplace.vue";
import DashboardDocuments from "@/components/dashboard/cards/DashboardDocuments.vue";
import DashboardManagerDB from "@/components/dashboard/cards/DashboardManagerDB.vue";
import DashboardInbox from "@/components/dashboard/cards/DashboardInbox.vue";
import { Company, Manager, Contractor, User, Team, Team_Bool_Exp, Project } from "@/generated/graphql";
import Roles from "@/entities/Roles";
import getActiveManagersCountCondition from "@/helpers/queryConditions/getActiveManagersCountCondition";
import DashboardCustomRequest from "@/components/dashboard/cards/DashboardRequest.vue";
import GET_PROJECT_GROUPS from "@/apollo/teams/GetProjectGroups.gql"
import LIST_TEAMS from "@/apollo/teams/ListTeams.gql";
import GroupsFiltersQuery from "@/entities/filters/GroupsFiltersQuery";
import { cloneDeep, isEqual } from "lodash-es";
import groupsFiltersTemplate from "@/data/filtersTemplates/groupsFiltersTemplate";

interface GroupsQueryVariables {

    where: Team_Bool_Exp,
  }

@Component({
  components: {
    DashboardCustomRequest,
    LoadingOverlay,
    DashboardContractors,
    DashboardContractorsManagerView,
    DashboardManagers,
    DashboardFavorites,
    DashboardProjects,
    DashboardGroups,
    DashboardPostings,
    DashboardMarketplace,
    DashboardDocuments,
    DashboardInbox,
    DashboardManagerDB,
  },
  apollo: {
    dashboard: {
      query: GET_DASHBOARD,
      variables() {
        return this.filters;
      },
      skip() {
        return this.skipQuery;
      },
      update: (data) => data,
    },
    groups: {
        query: GET_PROJECT_GROUPS,
        variables(): GroupsQueryVariables {
          return {
            where: {
              projects: {
                project_id: {
                  _eq: this.project?.id,
                },
              },
            },
          };
        },
        update(data) {
          this.paginationConfig.total = data.total.aggregate.count;
          return data.team;
        },
      },
      messaging: {
        query: LIST_TEAMS,
      variables() {
        return this.groupFilter;
      },
      skip() {
        return this.skipQuery;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.team.length;
        return data.team;
      },
      }
  },
})
export default class Dashboard extends Vue {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  // @Prop({ type: Object, default: undefined }) readonly project!: Project;
  @Prop({ type: Object, default: undefined }) readonly project?: Project;


  // @Prop({ type: Array, default: () => [] }) readonly groupsTable!: Team[]

  groups: Team[] | null = null;
  groupFilters: GroupsFiltersQuery = cloneDeep(groupsFiltersTemplate);


  skipQuery: boolean = false;
  dashboard: any;

  get accessAllowedManagersCount(): number | undefined {
    if (this.isManagersCountUnlimited) {
      return;
    }
    return this.dashboard?.subscriptions[0].subscription_plan.subscription_plan_access_permission.managers_count;
  }

  get activeManagersCount(): number | undefined {
    if (this.isManagersCountUnlimited) {
      return;
    }

    return this.dashboard?.activeManagersCount.aggregate.count;
  }

  get isManagersCountUnlimited(): boolean {
    return !this.currentUser.subscription_required;
  }

  get isAdmin(): boolean {
    return this.currentUser.role == Roles.ADMIN;
  }

  get isManager(): boolean {
    return this.currentUser.role == Roles.MANAGER;
  }

  get currentUserManagerId(): undefined | string {
    if (!this.isManager) {
      return undefined;
    }

    return this.currentUser?.manager?.id;
  }

  get filters(): object {
    const contractorConditions: object[] = [
      {
        approved: { _eq: true },
      },
      {
        deleted: { _eq: false },
      },
    ];

    return {
      isManagersCountUnlimited: this.isManagersCountUnlimited,
      activeManagersCountCondition: getActiveManagersCountCondition(this.company.id),
      companyId: this.company.id,

      contractorFilter: {
        _and: contractorConditions,
      },
      marketplaceFilter: {
        _and: [
          {
            marketplace: { _eq: true },
          },
          {
            created_at: {
              _gte: timeRangeForNew(this.$moment),
            },
          },
        ],
      },
      inboxFilter: {
        read: { _eq: false },
      },
      newMessagesFilter: {
        _and: [
          {
            created_at: {
              _gte: timeRangeForNew(this.$moment),
            },
          },
          {
            read: {_eq: false },
          },
        ],
      },
      newContractorFilter: {
        _and: [
          {
            deleted: { _eq: false },
          },
          {
            approved: { _eq: true },
          },
          {
            created_at: {
              _gte: timeRangeForNew(this.$moment),
            },
          },
        ],
      },
      requestsFilter: {
        _and: [
          {
            approved: { _eq: false },
          },
          {
            contractor: {
              deleted: { _eq: false },
            },
          },
        ],
      },
      approvedContractorsFilter: {
        _and: [
          {
            approved: { _eq: true },
          },
          {
            contractor: {
              deleted: { _eq: false },
            },
          },
        ],
      },
    };
  };


  get loading(): boolean {
    return this.$apollo.queries.dashboard.loading;
  }

  get managers(): Manager[] {
    return this.dashboard?.managers ?? [];
  }

  get contractors(): Contractor[] {
    return this.dashboard?.contractors ?? [];
  }

  get contractorsAvatar(): Contractor[] {
    return this.dashboard?.newContractorAvatar ?? [];
  }

  get marketplaceNew(): Contractor[] {
    return this.dashboard?.marketplaceNew ?? [];
  }

  get contractorsTotal(): number | undefined {
    return this.dashboard?.contractors_total?.aggregate?.count;
  }

  get newContractorsTotal(): number | undefined {
    return this.dashboard?.new_contractors_total?.aggregate?.count;
  }

  get managersTotal(): number | undefined {
    return this.dashboard?.managers_total?.aggregate?.count;
  }

  get requestsTotal(): number | undefined {
    return this.dashboard?.requests_total?.aggregate?.count;
  }

  get newMarketPlaceTotal(): number | undefined {
    return this.dashboard?.newMarketPlaceTotal?.aggregate?.count;
  }
  refreshPage(): void {
    this.$apollo.queries.dashboard.refetch()
  }
}
